"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authService, type UserMe } from "@/services/auth";

const AUTH_LOAD_TIMEOUT_MS = 18000;
const USER_CACHE_KEY = "appstok_me";

function writeUserCache(user: UserMe): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
  } catch {
    /* ignore */
  }
}

function clearUserCache(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(USER_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

type SessionCache =
  | { phase: "empty" }
  | { phase: "loading"; promise: Promise<UserMe | null> }
  | { phase: "ready"; user: UserMe | null };

let sessionCache: SessionCache = { phase: "empty" };

function loadSessionDeduped(): Promise<UserMe | null> {
  if (sessionCache.phase === "ready") return Promise.resolve(sessionCache.user);
  if (sessionCache.phase === "loading") return sessionCache.promise;
  const promise = authService
    .getMe()
    .then((u) => {
      sessionCache = { phase: "ready", user: u };
      return u;
    })
    .catch(() => {
      sessionCache = { phase: "ready", user: null };
      authService.clearLocalSession();
      clearUserCache();
      return null;
    });
  sessionCache = { phase: "loading", promise };
  return promise;
}

function invalidateSessionCache() {
  sessionCache = { phase: "empty" };
}

interface AuthContextValue {
  user: UserMe | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserMe | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      invalidateSessionCache();
      const me = await authService.getMe();
      sessionCache = { phase: "ready", user: me };
      setUser(me);
      writeUserCache(me);
    } catch {
      sessionCache = { phase: "ready", user: null };
      authService.clearLocalSession();
      clearUserCache();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const safetyId = setTimeout(() => {
      if (!alive) return;
      invalidateSessionCache();
      authService.clearLocalSession();
      setUser(null);
      clearUserCache();
      setIsLoading(false);
    }, AUTH_LOAD_TIMEOUT_MS);

    loadSessionDeduped()
      .then((me) => {
        if (!alive) return;
        setUser(me);
        if (me !== null) writeUserCache(me);
        else clearUserCache();
      })
      .finally(() => {
        clearTimeout(safetyId);
        if (!alive) return;
        setIsLoading(false);
      });

    return () => {
      alive = false;
      clearTimeout(safetyId);
    };
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    await authService.login(identifier, password);
    const me = await authService.getMe();
    sessionCache = { phase: "ready", user: me };
    writeUserCache(me);
    setUser(me);
  }, []);

  const logout = useCallback(async () => {
    invalidateSessionCache();
    clearUserCache();
    await authService.logout();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated: !!user, login, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
